import React, { useCallback, useEffect, useState } from 'react';
import { FiCheckCircle, FiChevronDown, FiChevronUp, FiCheckSquare, FiSquare } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';

export default function MonthCloseChecklist({ modoOscuro, onNavigate }) {
  const { secureFetch } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sendingPack, setSendingPack] = useState(false);
  const [packMessage, setPackMessage] = useState({ type: '', text: '' });

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureFetch('/api/reportes/cierre-mes');
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const sendPackToContador = async () => {
    if (!data || sendingPack) return;
    setSendingPack(true);
    setPackMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/reportes/cierre-mes/enviar-contador', { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'No se pudo enviar el pack');
      setPackMessage({ type: 'success', text: json.message || 'Pack enviado.' });
      await load();
    } catch (err) {
      setPackMessage({ type: 'error', text: err.message || 'Error al enviar' });
    } finally {
      setSendingPack(false);
    }
  };

  const toggleManual = async (id, done) => {
    if (!data || saving) return;
    const manualIds = (data.items || [])
      .filter((i) => i.manualDone && i.id !== id)
      .map((i) => i.id);
    const nextIds = done ? [...manualIds, id] : manualIds;
    setSaving(true);
    try {
      await secureFetch('/api/reportes/cierre-mes', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mes: data.mes, doneIds: nextIds }),
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className={`rounded-2xl border p-4 ${box}`}>
        <p className={`text-xs ${textMuted}`}>Cargando cierre de mes…</p>
      </section>
    );
  }

  if (!data?.items?.length) return null;

  const allDone = data.completed === data.total;
  const label = data.label ? data.label.charAt(0).toUpperCase() + data.label.slice(1) : data.mes;

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`} id="month-close-checklist">
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <div className="min-w-0 flex-1">
          <h2 className={`text-sm font-semibold ${textPri}`}>
            {allDone ? `Cierre de ${label} listo` : `Cierre de mes · ${label}`}
          </h2>
          <p className={`mt-0.5 text-xs ${textSec}`}>
            {allDone
              ? 'Completaste los pasos de cierre. Podés revisar el pack contador cuando quieras.'
              : `${data.completed} de ${data.total} pasos · ${data.percent}% completado`}
          </p>
          <div className={`mt-2 h-1.5 w-full overflow-hidden rounded-full ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <div
              className="h-full rounded-full bg-[#1F80FF] transition-all duration-300"
              style={{ width: `${data.percent}%` }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
            modoOscuro
              ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {collapsed ? <FiChevronDown className="h-3.5 w-3.5" /> : <FiChevronUp className="h-3.5 w-3.5" />}
          {collapsed ? 'Mostrar' : 'Ocultar'}
        </button>
      </div>

      {!collapsed && (
        <div className={`border-t px-4 py-4 sm:px-5 ${modoOscuro ? 'border-gray-700/80' : 'border-gray-200'}`}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((item) => {
              const isManual = !item.autoDone;

              return (
                <div
                  key={item.id}
                  className={`rounded-xl border p-3 transition-colors ${
                    item.done
                      ? modoOscuro
                        ? 'border-green-700/60 bg-green-950/20'
                        : 'border-green-200 bg-green-50/70'
                      : modoOscuro
                        ? 'border-gray-700'
                        : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {isManual ? (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => toggleManual(item.id, !item.done)}
                        className="mt-0.5 shrink-0 disabled:opacity-60"
                        title={item.done ? 'Desmarcar' : 'Marcar como hecho'}
                      >
                        {item.done ? (
                          <FiCheckSquare className="w-4 h-4 text-[#1F80FF]" />
                        ) : (
                          <FiSquare className={`w-4 h-4 ${textMuted}`} />
                        )}
                      </button>
                    ) : (
                      <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center">
                        <FiCheckCircle className="h-4 w-4 text-green-600" />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <span className={`block text-sm font-semibold ${textPri}`}>{item.label}</span>
                      <span className={`mt-0.5 block text-xs ${textMuted}`}>{item.hint}</span>
                      {item.href && (
                        <button
                          type="button"
                          onClick={() => onNavigate(item.href)}
                          className="mt-2 text-[10px] font-semibold text-[#1F80FF] hover:underline"
                        >
                          Ir →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border px-3 py-3 ${
            modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50/80'
          }`}>
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${textPri}`}>Cerrar mes con tu contador</p>
              <p className={`text-xs mt-0.5 ${textMuted}`}>
                Enviá el pack ZIP por email (configurá el contador en Cuenta → Notificaciones).
              </p>
              {packMessage.text && (
                <p className={`text-xs mt-1 ${packMessage.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {packMessage.text}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                disabled={sendingPack}
                onClick={sendPackToContador}
                className="rounded-xl bg-[#1F80FF] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1866CC] disabled:opacity-60"
              >
                {sendingPack ? 'Enviando…' : 'Enviar pack al contador'}
              </button>
              <button
                type="button"
                onClick={() => onNavigate('/contador')}
                className={`rounded-xl border px-3 py-2 text-xs font-semibold ${
                  modoOscuro ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-800'
                }`}
              >
                Vista contador
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
